import React, { useState, useRef } from "react";
import {
  Flame,
  FileText,
  UserPlus,
  TrendingUp,
  Bell,
  User,
  Filter,
  Search,
} from "lucide-react";
import { getFormattedDate } from "../../utils/formatDate";
import { TALENT_FUNNEL_DEFAULTS } from "../../models";
import { useAuth } from "../../context/AuthContext";
import UserProfilePopup from "../layout/UserProfilePopup";
import TalentSuggestionCard from "./TalentSuggestionCard";

const BAR_COLORS = ["bg-green-500", "bg-blue-500", "bg-red-500", "bg-purple-500"];

const TalentScoutingDashboard = ({
  scoutProfile,
  trackingData,
  // Métricas derivadas de datos vivos (ver utils/scoutDashboardMetrics). Si no
  // llegan, se cae al blob `scout_tracking.metrics` como antes.
  metrics: liveMetrics,
  suggestedAthletes = [],
  contactRequestsByAthleteId = {},
  onNotificationsClick,
  onSearchClick,
  onOpenProfile,
  onContactAthlete,
  onOpenAthlete,
}) => {
  const { signOut } = useAuth();
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const profileButtonRef = useRef(null);

  if (!scoutProfile || !trackingData) {
    return (
      <div className="p-4 space-y-6 animate-pulse bg-gray-50 h-full sm:p-6 lg:p-8">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-64 bg-gray-200 rounded-xl"></div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const { metrics } = trackingData;
  const funnel =
    liveMetrics?.talent_funnel || metrics?.talent_funnel || TALENT_FUNNEL_DEFAULTS;
  const topSports = liveMetrics?.top_sports || metrics?.top_sports || [];
  const suggestions = Array.isArray(suggestedAthletes) ? suggestedAthletes : [];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 h-full">
      <div className="p-4 space-y-6 sm:p-6 lg:p-8">
        {/* Header: greeting + (desktop-only) action icons */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-gray-500 font-medium text-sm">
              {getFormattedDate()}
            </p>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Bienvenido de vuelta, {scoutProfile.full_name?.split(" ")[0]}
            </h1>
          </div>

          <div className="hidden items-center gap-3 relative z-10 lg:flex">
            <button
              type="button"
              onClick={onSearchClick}
              data-analytics-id="scout-search"
              className="p-2.5 bg-white hover:bg-gray-100 rounded-xl transition-colors shadow-sm border border-gray-100 text-gray-600"
              aria-label="Buscar atletas"
            >
              <Search size={20} />
            </button>
            <button
              onClick={onNotificationsClick}
              data-analytics-id="scout-notifications"
              className="p-2.5 bg-white hover:bg-gray-100 rounded-xl transition-colors shadow-sm border border-gray-100 text-gray-600 relative"
            >
              <Bell size={20} />
              <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            </button>
            <button
              ref={profileButtonRef}
              onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)}
              data-analytics-id="scout-profile-menu"
              className="p-2.5 bg-blue-900 hover:bg-blue-800 text-white rounded-xl transition-colors shadow-sm flex items-center justify-center"
            >
              <User size={20} />
            </button>

            <UserProfilePopup
              isOpen={isProfilePopupOpen}
              onClose={() => setIsProfilePopupOpen(false)}
              buttonRef={profileButtonRef}
              userData={scoutProfile}
              onLogout={signOut}
              onViewProfile={() => {
                if (onOpenProfile) onOpenProfile();
                setIsProfilePopupOpen(false);
              }}
            />
          </div>
        </div>

        {/* INICIO hero: display heading + subtitle, with the "new talents" pill */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-5xl font-extrabold uppercase tracking-tight text-gray-900 sm:text-6xl">
              Inicio
            </h2>
            <p className="mt-1 text-sm text-gray-500 sm:text-base">
              Visualiza, Filtra y Conecta
            </p>
          </div>
          <div className="mt-1 inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-green-200 bg-green-100 px-3 py-1.5 text-green-800 shadow-sm">
            <TrendingUp size={15} />
            <span className="whitespace-nowrap text-xs font-bold">
              {funnel.new_talents > 0 ? `${funnel.new_talents} Nuevos` : "Al día"}
            </span>
          </div>
        </div>

        {/* Talentos Que Te Podrían Interesar — horizontal carousel on mobile */}
        {suggestions.length > 0 && (
          <section>
            <h3 className="mb-3 text-lg font-bold text-gray-900">
              Talentos Que Te Podrían Interesar
            </h3>
            <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide sm:mx-0 sm:px-0 lg:flex-wrap lg:overflow-visible">
              {suggestions.slice(0, 12).map((player) => (
                <TalentSuggestionCard
                  key={player.id ?? player.athlete_id ?? player.full_name}
                  player={player}
                  contactRequest={
                    contactRequestsByAthleteId[player.id ?? player.athlete_id]
                  }
                  onContact={onContactAthlete}
                  onClick={() => onOpenAthlete?.(player)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Análisis de Talentos (Embudo) */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              Análisis de Talentos
            </h3>
            <button className="text-gray-400 hover:text-blue-600">
              <Filter size={18} />
            </button>
          </div>

          <div className="flex items-center justify-around py-2 sm:py-4">
            <div className="flex flex-col items-center gap-1 flex-1 border-r border-gray-100">
              <div className="p-2.5 bg-orange-50 rounded-full mb-1 sm:p-3 sm:mb-2">
                <Flame className="text-orange-500" size={22} />
              </div>
              <div className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {funnel.new_talents}
              </div>
              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide sm:text-xs">
                Nuevos
              </div>
            </div>

            <div className="flex flex-col items-center gap-1 flex-1 border-r border-gray-100">
              <div className="p-2.5 bg-blue-50 rounded-full mb-1 sm:p-3 sm:mb-2">
                <FileText className="text-blue-500" size={22} />
              </div>
              <div className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {funnel.evaluating}
              </div>
              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide sm:text-xs">
                Evaluando
              </div>
            </div>

            <div className="flex flex-col items-center gap-1 flex-1">
              <div className="p-2.5 bg-green-50 rounded-full mb-1 sm:p-3 sm:mb-2">
                <UserPlus className="text-green-500" size={22} />
              </div>
              <div className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {funnel.contacted}
              </div>
              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide sm:text-xs">
                Contactados
              </div>
            </div>
          </div>
        </div>

        {/* TOP DEPORTES — el panel Ratings se ocultó: ver [[scout-ratings-open-decision]] */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 sm:p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6">
            Top Deportes de Talentos
          </h3>
          <div className="space-y-5">
            {topSports.length === 0 && (
              <p className="text-sm text-gray-400">
                Aún no hay datos de deportes.
              </p>
            )}
            {topSports.map((sportItem, index) => {
              const color = BAR_COLORS[index % BAR_COLORS.length];
              const percent = Math.min(
                (sportItem.score / sportItem.max_score) * 100,
                100,
              );
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {sportItem.sport}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      {sportItem.score}/{sportItem.max_score}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div
                      className={`${color} h-2.5 rounded-full transition-all duration-1000`}
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentScoutingDashboard;
